import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Typography,
} from '@mui/material';
import { SupplierPaymentService } from '../../services/supplierPayment.service';
import {
  SupplierPayment, SupplierPaymentStatus, SupplierPaymentEdit,
} from '../../interfaces/procurement/supplierPayment.interface';
import { ReverseConfirmDialog } from './ReverseConfirmDialog';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { EditHistoryPanel, EditHistoryRow } from './EditHistoryPanel';

const EDIT_ROLES = ['Accountant', 'InstitutionAdmin'];

const STATUS_COLORS: Record<SupplierPaymentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  POSTED: 'success',
  REVERSED: 'error',
};

export const SupplierPaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [payment, setPayment] = useState<SupplierPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [editHistoryRows, setEditHistoryRows] = useState<EditHistoryRow[]>([]);

  useEffect(() => {
    if (!id) return;
    SupplierPaymentService.getById(id)
      .then(p => {
        setPayment(p);
        if ((p.editCount ?? 0) > 0) {
          SupplierPaymentService.editHistory(id)
            .then((edits: SupplierPaymentEdit[]) => {
              setEditHistoryRows(edits.map(e => ({
                editSeq: e.editSeq,
                editedAt: e.editedAt,
                editReason: e.editReason,
                summary: `amount ${e.prevAmount} → ${p.amount}`,
              })));
            })
            .catch(() => setEditHistoryRows([]));
        }
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier payment'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReverse = async () => {
    if (!payment) return;
    setReversing(true);
    try {
      const updated = await SupplierPaymentService.reverse(payment.id);
      setPayment(updated);
      toast.showSuccess('Supplier payment reversed');
      setReverseDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse payment');
    } finally {
      setReversing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!payment) return <Box sx={{ p: 3 }}><Typography>Supplier payment not found.</Typography></Box>;

  // The JWT `roles` claim is a single string, not an array; normalise so the
  // role check below (which uses Array methods) doesn't throw.
  const rawRoles = AuthService.getCurrentUser()?.roles;
  const userRoles: string[] = Array.isArray(rawRoles) ? rawRoles : rawRoles ? [rawRoles] : [];
  const canReverse = payment.status === 'POSTED';
  const canEdit = payment.status === 'POSTED' && userRoles.some(r => EDIT_ROLES.includes(r));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{payment.paymentNumber}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/procurement/supplier-payments/${payment.id}/edit`)}
            >
              Edit
            </Button>
          )}
          {canReverse && (
            <Button variant="outlined" color="error" onClick={() => setReverseDialogOpen(true)}>
              Reverse
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Payment Number</Typography>
            <Typography>{payment.paymentNumber}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Chip label={payment.status} color={STATUS_COLORS[payment.status]} size="small" />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Supplier Bill ID</Typography>
            <Typography>{payment.supplierBillId}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Payment Date</Typography>
            <Typography>{payment.paymentDate}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Amount</Typography>
            <Typography>{payment.amount}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Payment Account ID</Typography>
            <Typography>{payment.paymentAccountId}</Typography>
          </Box>
          {payment.paymentMethod && (
            <Box>
              <Typography variant="body2" color="text.secondary">Payment Method</Typography>
              <Typography>{payment.paymentMethod}</Typography>
            </Box>
          )}
          {payment.reference && (
            <Box>
              <Typography variant="body2" color="text.secondary">Reference</Typography>
              <Typography>{payment.reference}</Typography>
            </Box>
          )}
          {payment.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal Entry ID</Typography>
              <Typography>{payment.journalEntryId}</Typography>
            </Box>
          )}
          {payment.status === 'REVERSED' && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed By</Typography>
                <Typography>{payment.reversedById ?? '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed Date</Typography>
                <Typography>{payment.reversedDate ?? '-'}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {(payment.editCount ?? 0) > 0 && (
        <EditHistoryPanel title="Edit history" rows={editHistoryRows} />
      )}

      <ReverseConfirmDialog
        open={reverseDialogOpen}
        entityLabel="Supplier Payment"
        consequence="Cash and AP movements will be reversed. The associated bill will move back to UNPAID or PARTIALLY_PAID depending on remaining payments."
        onCancel={() => setReverseDialogOpen(false)}
        onConfirm={handleReverse}
        loading={reversing}
      />
    </Box>
  );
};

export default SupplierPaymentDetail;
